import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { connect } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
} from "@material-ui/core";
import VideocamIcon from "@material-ui/icons/VideocamRounded";
import CheckCircleIcon from "@material-ui/icons/CheckCircleRounded";
import { makeStyles } from "@material-ui/core/styles";
import { showSnackbar } from "../../utilities/UtilitiesActions";
import {
  getGoogleCalendarStatusRequest,
  getGoogleConsentUrlRequest,
  disconnectGoogleCalendarRequest,
} from "./GoogleCalendarApiActions";

const useStyles = makeStyles((theme) => ({
  card: {
    borderRadius: 12,
    marginBottom: theme.spacing(3),
    borderLeft: "4px solid #f9a825",
  },
  cardConnected: {
    borderLeft: "4px solid #2e7d32",
  },
  content: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  icon: {
    fontSize: 32,
    color: "#f9a825",
  },
  iconConnected: {
    color: "#2e7d32",
  },
  text: {
    flex: 1,
    minWidth: 240,
  },
  title: {
    fontWeight: 600,
    fontSize: 15,
  },
  subtitle: {
    fontSize: 13,
    color: theme.palette.text.secondary,
  },
}));

/**
 * One-time Google Calendar connection for the logged-in doctor.
 *
 * An online consultation needs a Google Meet link, and Google only lets the
 * account that created a meeting host it — so the meeting has to be created as
 * the doctor. This card is where the doctor grants that access, once. After
 * connecting, every online booking of theirs gets a Meet link automatically.
 *
 * Hidden entirely once connected and dismissed-by-status, so a connected doctor
 * sees a small confirmation rather than a call to action.
 */
const GoogleCalendarCard = ({
  fetchStatus,
  fetchConsentUrl,
  disconnect,
  notify,
}) => {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);

  const loadStatus = useCallback(() => {
    return fetchStatus().then((result) => {
      setStatus(result);
      setLoading(false);
    });
  }, [fetchStatus]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // The backend callback returns the doctor here with ?google=connected|failed.
  // Report it, drop the param so a refresh does not repeat the message, and
  // re-read the status.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const outcome = params.get("google");
    if (!outcome) return;

    if (outcome === "connected") {
      notify("Google Calendar connected. Online consultations will now include a Meet link.", "success");
    } else {
      notify("Google Calendar was not connected. Please try again.", "error");
    }

    params.delete("google");
    history.replace({
      pathname: location.pathname,
      search: params.toString() ? `?${params.toString()}` : "",
    });
    loadStatus();
  }, [location.search, location.pathname, history, notify, loadStatus]);

  const handleConnect = () => {
    setRedirecting(true);
    fetchConsentUrl()
      .then((url) => {
        // Full redirect: Google returns to the backend callback, which sends the
        // doctor back here with ?google=connected.
        window.location.href = url;
      })
      .catch(() => setRedirecting(false));
  };

  const handleDisconnect = () => {
    disconnect().then(loadStatus, () => {});
  };

  // Nothing to say while the status is unknown, or if the endpoint is unavailable
  // (older backend / non-doctor role) — the card simply does not render.
  if (loading || !status) return null;

  if (status.connected) {
    return (
      <Card className={`${classes.card} ${classes.cardConnected}`} elevation={0}>
        <CardContent className={classes.content}>
          <CheckCircleIcon className={`${classes.icon} ${classes.iconConnected}`} />
          <Box className={classes.text}>
            <Typography className={classes.title}>
              Google Calendar connected
            </Typography>
            <Typography className={classes.subtitle}>
              {status.googleEmail
                ? `Meet links for your online consultations are created from ${status.googleEmail}.`
                : "Meet links for your online consultations are created automatically."}
            </Typography>
          </Box>
          <Button size="small" color="primary" onClick={handleDisconnect}>
            Disconnect
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={classes.card} elevation={0}>
      <CardContent className={classes.content}>
        <VideocamIcon className={classes.icon} />
        <Box className={classes.text}>
          <Typography className={classes.title}>
            Connect Google Calendar for video consultations
          </Typography>
          <Typography className={classes.subtitle}>
            One-time sign-in with your Google account. After this, every online
            appointment gets a Google Meet link automatically — with you as the
            host — and the link is sent to the patient.
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={handleConnect}
          disabled={redirecting}
          startIcon={redirecting ? <CircularProgress size={16} /> : null}
        >
          {redirecting ? "Opening Google…" : "Connect Google Calendar"}
        </Button>
      </CardContent>
    </Card>
  );
};

const mapDispatchToProps = (dispatch) => ({
  fetchStatus: () => dispatch(getGoogleCalendarStatusRequest()),
  fetchConsentUrl: () => dispatch(getGoogleConsentUrlRequest()),
  disconnect: () => dispatch(disconnectGoogleCalendarRequest()),
  notify: (message, variant) => dispatch(showSnackbar(message, variant)),
});

export default connect(null, mapDispatchToProps)(GoogleCalendarCard);
